import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Popover,
} from '@mui/material';
import { ColorPicker } from '~/components/ColorPicker';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleCreateUser, handleGetUsers, handleUpdateUser, handleDeleteUsers } from '~/thunks/userManagement/userManagementThunk';
import { IUser } from '~/apis/userManagement';
import styles from './UserManagement.module.scss';
import PrimaryButton from '~/components/common/primary-button/PrimaryButton';
import { PlusIcon } from 'lucide-react';

// Role mapping function
const getRoleDisplayName = (role: string): string => {
  const roleMap: { [key: string]: string } = {
    '00001': 'Admin',
    '00002': 'Sales',
    '00003': 'Production',
    '00004': 'Subcontractor',
  };

  return roleMap[role] || role;
};

// Status display function - capitalize first letter only
const getStatusDisplayName = (status: string): string => {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const UserManagement = () => {
  const dispatch = useAppDispatch();
  const { users, loading, error } = useAppSelector(state => state.userManagement);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<IUser | null>(null);
  const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<IUser | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '00002',
    color: '#FA6363',
    status: 'ACTIVE' as 'ACTIVE' | 'INACTIVE',
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    phone: '',
  });

  // Load users from API when component mounts
  useEffect(() => {
    dispatch(handleGetUsers());
  }, [dispatch]);

  const handleEditUser = (user: IUser) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phoneNumber || '',
      role: user.role || '00002',
      color: user.color || '#FA6363',
      status: user.status || 'ACTIVE',
    });
    setErrors({ name: '', email: '', phone: '' });
    setOpenDialog(true);
  };

  const handleDeleteUser = (user: IUser) => {
    setUserToDelete(user);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      dispatch(handleDeleteUsers({ ids: [userToDelete.id] })).then(() => {
        setOpenDeleteModal(false);
        setUserToDelete(null);
        dispatch(handleGetUsers());
      });
    }
  };

  const handleSaveUser = () => {
    // Validate name, email and phone number
    const newErrors = { name: '', email: '', phone: '' };
    let hasError = false;

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      hasError = true;
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      hasError = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
      hasError = true;
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setErrors(newErrors);

    const name = formData.name.trim();
    const validRoles = ['00001', '00002', '00003', '00004'];
    const roleValue = validRoles.includes(formData.role) ? formData.role : '00002';

    const payload = {
      email: formData.email,
      role: roleValue,
      status: formData.status,
      profile: {
        name,
        phoneNumber: formData.phone.trim(),
        color: formData.color || '#FA6363'
      }
    };

    if (editingUser) {
      // Update existing user
      dispatch(handleUpdateUser({ id: editingUser.id, payload })).then(() => {
        setOpenDialog(false);
        setEditingUser(null);
        setErrors({ name: '', email: '', phone: '' });
        dispatch(handleGetUsers());
      });
    } else {
      // Create new user
      dispatch(handleCreateUser(payload as any)).then(() => {
        setOpenDialog(false);
        setErrors({ name: '', email: '', phone: '' });
        dispatch(handleGetUsers());
      });
    }
  };

  // Show loading spinner while fetching data
  if (loading && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={styles.userManagement}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box className={styles.header}>
        <Typography variant="h5" className={styles.title}>
          User Management
        </Typography>

        <PrimaryButton
          title="Add User"
          icon={<PlusIcon className={styles.addUserIcon} />}
          onClick={() => {
            setEditingUser(null);
            setFormData({
              name: '',
              email: '',
              phone: '',
              role: '00002',
              color: '#FA6363',
              status: 'ACTIVE'
            });
            setErrors({ name: '', email: '', phone: '' });
            setOpenDialog(true);
          }}
          className={styles.addButton}
        />
      </Box>

      <div className={styles.tableWrapper}>
        <TableContainer component={Paper} className={styles.tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={styles.tableHeader}>User</TableCell>
                <TableCell className={styles.tableHeader}>Email</TableCell>
                <TableCell className={styles.tableHeader}>Role</TableCell>
                <TableCell className={styles.tableHeader} align="center">Color</TableCell>
                <TableCell className={styles.tableHeader}>Status</TableCell>
                <TableCell className={styles.tableHeader}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className={styles.tableRow}>
                  <TableCell>
                    <Box className={styles.userCell}>
                      <Avatar
                        className={styles.avatar}
                        sx={{
                          backgroundColor: user.avatar ? '#1976d2' : '#6B7280',
                          backgroundImage: user.avatar ? `url(${user.avatar})` : 'none'
                        }}
                      />
                      <Typography className={styles.userName}>
                        {user.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell className={styles.emailCell}>
                    {user.email}
                  </TableCell>
                  <TableCell className={styles.roleCell}>
                    {getRoleDisplayName(user.role)}
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      className={styles.colorDot}
                      sx={{ backgroundColor: user.color || formData.color }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusDisplayName(user.status)}
                      size="small"
                      sx={{
                        backgroundColor: user.status === 'ACTIVE' ? '#10B981' : '#949699',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 500,
                        '& .MuiChip-label': {
                          color: 'white',
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box className={styles.actions}>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleEditUser(user);
                        }}
                        className={styles.editButton}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteUser(user);
                        }}
                        className={styles.deleteButton}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Add/Edit User Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setEditingUser(null);
          setErrors({ name: '', email: '', phone: '' });
        }}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '500px',
            maxWidth: '90vw',
            height: 'auto',
            maxHeight: '90vh',
            backgroundColor: '#071427',
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            margin: '24px',
            '@media (max-height: 800px)': {
              maxHeight: '95vh',
              margin: '16px',
            },
          }
        }}
      >
        <DialogTitle className={styles.dialogTitle}>
          <Box className={styles.dialogTitleContent}>
            <Typography variant="h6" className={styles.dialogTitleText}>
              {editingUser ? 'Edit User' : 'Add New User'}
            </Typography>
            <IconButton
              onClick={() => setOpenDialog(false)}
              className={styles.closeButton}
              size="small"
            >
              ✕
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{
          backgroundColor: '#071427',
          padding: '16px 24px',
          flex: '1 1 auto',
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          maxHeight: 'calc(90vh - 180px)',
          '@media (max-height: 800px)': {
            maxHeight: 'calc(95vh - 160px)',
            padding: '12px 24px',
          },
        }}>
          <Box className={styles.form} sx={{ paddingBottom: '16px' }}>
            <Typography variant="body1" className={styles.fieldLabel}>
              Name <span style={{ color: '#ef4444' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              required
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (errors.name) {
                  setErrors({ ...errors, name: '' });
                }
              }}
              error={Boolean(errors.name)}
              helperText={errors.name}
              className={styles.formField}
              InputProps={{
                placeholder: 'Enter full name',
                className: styles.inputField
              }}
            />

            <Typography variant="body1" className={styles.fieldLabel}>
              Email <span style={{ color: '#ef4444' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              required
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (errors.email) {
                  setErrors({ ...errors, email: '' });
                }
              }}
              error={Boolean(errors.email)}
              helperText={errors.email}
              className={styles.formField}
              InputProps={{
                placeholder: 'Enter email address',
                className: styles.inputField
              }}
            />

            <Typography variant="body1" className={styles.fieldLabel}>
              Phone Number <span style={{ color: '#ef4444' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              required
              value={formData.phone}
              onChange={(e) => {
                setFormData({ ...formData, phone: e.target.value });
                if (errors.phone) {
                  setErrors({ ...errors, phone: '' });
                }
              }}
              error={Boolean(errors.phone)}
              helperText={errors.phone}
              className={styles.formField}
              InputProps={{
                placeholder: 'Enter phone number',
                className: styles.inputField
              }}
            />

            <Typography variant="body1" className={styles.fieldLabel}>
              Select Role
            </Typography>
            <FormControl fullWidth className={styles.formField}>
              <Select
                value={formData.role || ''}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                displayEmpty
                className={styles.selectField}
                sx={{
                  color: '#FFFFFF !important',
                  '& .MuiSelect-select': {
                    color: '#FFFFFF !important',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1F2937 !important',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1F2937 !important',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1F2937 !important',
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Choose a role
                </MenuItem>
                <MenuItem value="00001">Admin</MenuItem>
                <MenuItem value="00002">Sales</MenuItem>
                <MenuItem value="00003">Production</MenuItem>
                <MenuItem value="00004">Subcontractor</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="body1" className={styles.fieldLabel}>
              Select Color
            </Typography>
            <Box className={styles.formField}>
              <Box
                className={styles.colorPickerButton}
                onClick={(e) => setColorPickerAnchor(e.currentTarget)}
              >
                <Box
                  className={styles.colorSwatch}
                  sx={{ backgroundColor: formData.color }}
                />
              </Box>

              <Popover
                open={Boolean(colorPickerAnchor)}
                anchorEl={colorPickerAnchor}
                onClose={() => setColorPickerAnchor(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  sx: {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    padding: 0
                  }
                }}
              >
                <ColorPicker
                  color={formData.color}
                  onChange={(color: string) => setFormData({ ...formData, color })}
                  onClose={() => setColorPickerAnchor(null)}
                />
              </Popover>
            </Box>

            <Typography variant="body1" className={styles.fieldLabel}>
              Status
            </Typography>
            <FormControl fullWidth className={styles.formField}>
              <Select
                value={formData.status || 'ACTIVE'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'ACTIVE' | 'INACTIVE' })}
                className={styles.selectField}
                sx={{
                  color: '#FFFFFF !important',
                  '& .MuiSelect-select': {
                    color: '#FFFFFF !important',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1F2937 !important',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1F2937 !important',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1F2937 !important',
                  },
                }}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => {
            setOpenDialog(false);
            setEditingUser(null);
            setErrors({ name: '', email: '', phone: '' });
          }} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button onClick={handleSaveUser} variant="contained" className={styles.addUserButton}>
            {editingUser ? 'Save Changes' : '+ Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ModalAlertDelete
        open={openDeleteModal}
        onClose={() => {
          setOpenDeleteModal(false);
          setUserToDelete(null);
        }}
        onCancel={() => {
          setOpenDeleteModal(false);
          setUserToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        description={`Are you sure you want to delete ${userToDelete?.name || 'this user'}? This action cannot be undone.`}
      />
    </Box>
  );
};

export default UserManagement;